import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import Lottie from 'lottie-react';
import animationData from '../assets/animations/liquid-orbit-1.json';
import { useAuth } from '../contexts/AuthContext';


/* ─────────────────────────────────────────────
   Lottie
───────────────────────────────────────────── */
const HeroLottie = () => (
    <LottieWrap>
        <Lottie
            animationData={animationData}
            loop
            autoplay
            style={{ width: '100%', height: '100%' }}
            rendererSettings={{
                preserveAspectRatio: 'xMidYMid meet',
                progressiveLoad: true,
                hideOnTransparent: true,
            }}
        />
    </LottieWrap>
);


/* ─────────────────────────────────────────────
   Page
───────────────────────────────────────────── */
const LandingPage = () => {
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <Page>
            {/* Nav */}
            <Nav $scrolled={scrolled}>
                <NavInner>
                    <Logo>ddiring</Logo>
                    <NavActions>
                        <NavLink onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>기능</NavLink>
                        <NavLink onClick={() => document.getElementById('how').scrollIntoView({ behavior: 'smooth' })}>이용 방법</NavLink>
                        {isLoggedIn
                            ? <LoginBtn onClick={() => navigate('/stores')}>내 스토어</LoginBtn>
                            : <LoginBtn onClick={() => navigate('/auth/login')}>로그인</LoginBtn>
                        }
                    </NavActions>
                </NavInner>
            </Nav>

            {/* Hero */}
            <Hero>
                <HeroLeft>
                    <Badge>✦&nbsp; 보이스 에이전트</Badge>
                    <HeroTitle>
                        일하다 놓친 전화,<br />
                        전부 <GradientText>예약</GradientText>이었습니다
                    </HeroTitle>
                    <HeroDesc>
                        매장에서 일하는 동안에도 고객은 전화합니다.<br />
                        띠링이 대신 받아 예약을 잡고, 캘린더에 기록합니다.<br />
                        사장님은 지금 하던 일에만 집중하세요.
                    </HeroDesc>
                    <HeroCtas>
                        <CtaPrimary onClick={() => navigate(isLoggedIn ? '/stores' : '/auth/login')}>
                            {isLoggedIn ? '내 스토어 바로가기' : '띠링과 대화하기'}
                            <CtaShimmer />
                        </CtaPrimary>
                        <CtaSecondary onClick={() => document.getElementById('how').scrollIntoView({ behavior: 'smooth' })}>
                            어떻게 작동하나요? →
                        </CtaSecondary>
                    </HeroCtas>
                    <TrustRow>
                        <TrustDot />
                        <TrustText>별도 앱 설치 없음</TrustText>
                        <TrustSep />
                        <TrustDot />
                        <TrustText>24시간 무중단</TrustText>
                        <TrustSep />
                        <TrustDot />
                        <TrustText>3분 설정</TrustText>
                    </TrustRow>
                </HeroLeft>
                <HeroRight>
                    <HeroLottie />
                </HeroRight>
            </Hero>

            {/* Stats */}
            <StatsBar>
                <StatsInner>
                    {STATS.map((s, i) => (
                        <>
                            <StatItem key={s.label}>
                                <StatValue>{s.value}</StatValue>
                                <StatLabel>{s.label}</StatLabel>
                            </StatItem>
                            {i < STATS.length - 1 && <StatDivider key={`div-${i}`} />}
                        </>
                    ))}
                </StatsInner>
            </StatsBar>

            {/* Use Cases */}
            <UseCaseSection>
                <SectionLabel>Use cases</SectionLabel>
                <SectionTitle>어떤 매장이든 다 해당돼요</SectionTitle>
                <UseCaseGrid>
                    {USE_CASES.map((c) => (
                        <UseCaseCard key={c.industry}>
                            <UseCaseHeader>
                                <UseCaseEmoji>{c.icon}</UseCaseEmoji>
                                <UseCaseIndustry>{c.industry}</UseCaseIndustry>
                            </UseCaseHeader>
                            <UseCasePain>"{c.pain}"</UseCasePain>
                            <UseCaseResult>
                                <UseCaseArrow>→</UseCaseArrow>
                                {c.result}
                            </UseCaseResult>
                        </UseCaseCard>
                    ))}
                </UseCaseGrid>
            </UseCaseSection>

            {/* Features */}
            <Section id="features">
                <SectionLabel>Features</SectionLabel>
                <SectionTitle>필요한 건 다 있어요</SectionTitle>
                <FeatureGrid>
                    {FEATURES.map((f) => (
                        <FeatureCard key={f.title} $accent={f.color}>
                            <FeatureIconWrap $color={f.color}>
                                <span>{f.icon}</span>
                            </FeatureIconWrap>
                            <FeatureTitle>{f.title}</FeatureTitle>
                            <FeatureDesc>{f.desc}</FeatureDesc>
                        </FeatureCard>
                    ))}
                </FeatureGrid>
            </Section>

            {/* How it works */}
            <AltSection id="how">
                <SectionLabel>How it works</SectionLabel>
                <SectionTitle>3분이면 충분해요</SectionTitle>
                <StepList>
                    {STEPS.map((s, i) => (
                        <StepItem key={s.title}>
                            <StepNum>{i + 1}</StepNum>
                            <StepConnector $visible={i < STEPS.length - 1} />
                            <StepBody>
                                <StepTitle>{s.title}</StepTitle>
                                <StepDesc>{s.desc}</StepDesc>
                            </StepBody>
                        </StepItem>
                    ))}
                </StepList>
            </AltSection>

            {/* CTA Banner */}
            <CtaBanner>
                <CtaBannerInner>
                    <CtaBannerTitle>지금 바로 시작해보세요</CtaBannerTitle>
                    <CtaBannerDesc>설정에 걸리는 시간은 단 3분, 그 다음은 AI가 알아서 합니다.</CtaBannerDesc>
                    <CtaPrimaryLarge onClick={() => navigate(isLoggedIn ? '/stores' : '/auth/login')}>
                        {isLoggedIn ? '내 스토어 바로가기' : '무료로 시작하기'}
                        <CtaShimmer />
                    </CtaPrimaryLarge>
                </CtaBannerInner>
            </CtaBanner>

            {/* Footer */}
            <Footer>
                <FooterLogo>ddiring</FooterLogo>
                <FooterText>© 2025 ddiring. AI 전화 예약 비서 서비스</FooterText>
            </Footer>
        </Page>
    );
};


/* ─────────────────────────────────────────────
   Data
───────────────────────────────────────────── */
const STATS = [
    { value: '1,200+', label: '매장 이용 중' },
    { value: '98%',    label: '예약 완료율' },
    { value: '3분',    label: '평균 설정 시간' },
    { value: '24/7',   label: '무중단 운영' },
];

const USE_CASES = [
    {
        icon: '✂️',
        industry: '미용실 · 헤어샵',
        pain: '손님 머리 하는 중에 예약 전화가 오면 받을 수가 없어요',
        result: '커트 중에도 AI가 전화를 받아 빈 시간에 예약을 잡아드립니다',
    },
    {
        icon: '🏥',
        industry: '의원 · 클리닉',
        pain: '진료 중에 접수 문의 전화가 계속 오는데 받을 수가 없어요',
        result: '진료 중에도 AI가 접수 안내와 예약을 자동으로 처리합니다',
    },
    {
        icon: '🍽️',
        industry: '음식점 · 카페',
        pain: '점심 피크 때 서빙하다가 예약 전화를 몇 번이나 놓쳤어요',
        result: '가장 바쁜 시간에도 AI가 예약 전화를 하나도 빠짐없이 받습니다',
    },
    {
        icon: '🎵',
        industry: '레슨 · 스튜디오',
        pain: '수업 중에는 전화를 못 받으니 문의가 그냥 끊겨버려요',
        result: '수업 중 걸려온 문의도 AI가 응대해 잠재 수강생을 놓치지 않습니다',
    },
];

const FEATURES = [
    { icon: '📞', color: '#487BFD', title: '24시간 자동 응대',   desc: '영업 시간 외에도 AI가 고객 전화를 받아 예약을 받고 기본 문의에 답합니다.' },
    { icon: '📅', color: '#10B981', title: '캘린더 자동 연동',   desc: '예약이 완료되면 내 캘린더에 자동으로 일정이 등록됩니다.' },
    { icon: '🧠', color: '#8B5CF6', title: '맞춤 AI 훈련',       desc: '메뉴, 가격, 특이사항을 AI에게 알려주면 더 정확하게 응대합니다.' },
    { icon: '💬', color: '#F59E0B', title: '채널별 관리',         desc: '헤어·네일·마사지처럼 예약 유형을 채널로 나눠 독립적으로 운영할 수 있어요.' },
    { icon: '🔔', color: '#EF4444', title: '실시간 알림',         desc: '새 예약이 들어오면 즉시 알림을 보내드립니다.' },
    { icon: '📊', color: '#06B6D4', title: '예약 현황 한눈에',   desc: '월별 캘린더 뷰로 예약 현황을 한눈에 파악하고 일정을 관리하세요.' },
];

const STEPS = [
    { title: '스토어 생성',    desc: '매장 이름과 카테고리를 입력하면\n전용 AI 전화번호가 배정됩니다.' },
    { title: '채널 설정',      desc: '예약 유형별로 채널을 만들고\nAI에게 매장 정보를 알려주세요.' },
    { title: 'AI가 대신 받아요', desc: '고객이 번호로 전화하면\nAI가 예약을 받고 캘린더에 기록합니다.' },
];


/* ─────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────── */
const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const shimmerSlide = keyframes`
    from { left: -80%; }
    to   { left: 140%; }
`;

const gradientShift = keyframes`
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;


/* ─────────────────────────────────────────────
   Styled Components
───────────────────────────────────────────── */
const Page = styled.div`
    width: 100%;
    background: #ffffff;
    color: #0a0a0a;
    font-family: Pretendard, -apple-system, sans-serif;
    overflow-x: hidden;
`;

/* Nav */
const Nav = styled.header`
    position: fixed;
    top: 0; left: 0;
    width: 100%;
    z-index: 200;
    transition: background 0.25s, backdrop-filter 0.25s, border-color 0.25s;
    ${({ $scrolled }) => $scrolled ? css`
        background: rgba(255,255,255,0.88);
        backdrop-filter: blur(16px);
        border-bottom: 1px solid #ebebeb;
    ` : css`
        background: transparent;
        border-bottom: 1px solid transparent;
    `}
`;

const NavInner = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 40px;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    @media (max-width: 576px) { padding: 0 20px; }
`;

const Logo = styled.div`
    font-size: 20px;
    font-weight: 800;
    color: #487BFD;
    letter-spacing: -0.02em;
`;

const NavActions = styled.div`
    display: flex;
    align-items: center;
    gap: 32px;
    @media (max-width: 576px) { gap: 16px; }
`;

const NavLink = styled.button`
    background: none;
    border: none;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #999999;
    cursor: pointer;
    padding: 0;
    transition: color 0.15s;
    &:hover { color: #0a0a0a; }
    @media (max-width: 576px) { display: none; }
`;

const LoginBtn = styled.button`
    background: none;
    border: 1px solid #dddddd;
    border-radius: 8px;
    padding: 8px 20px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #555555;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
    &:hover { border-color: #487BFD; color: #487BFD; }
`;

/* Hero */
const Hero = styled.section`
    min-height: 100dvh;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 40px;
    display: flex;
    align-items: center;
    gap: 40px;
    position: relative;

    /* 도트 그리드 + 방사형 그라디언트 */
    &::before {
        content: '';
        position: fixed;
        inset: 0;
        background-image:
            radial-gradient(ellipse 70% 60% at 65% 50%, rgba(72,123,253,0.07) 0%, transparent 70%),
            radial-gradient(circle, #d5e0ff 1px, transparent 1px);
        background-size: auto, 28px 28px;
        pointer-events: none;
        z-index: 0;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        padding: 120px 24px 60px;
        gap: 0;
    }
`;

const HeroLeft = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    animation: ${fadeUp} 0.7s ease both;
    position: relative;
    z-index: 1;
`;

const HeroRight = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 500px;
    position: relative;
    z-index: 1;
    @media (max-width: 768px) { min-height: 300px; margin-top: -40px; }
`;

const LottieWrap = styled.div`
    width: 100%;
    height: 600px;
    @media (max-width: 768px) { height: 320px; }
`;

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(72, 123, 253, 0.07);
    border: 1px solid rgba(72, 123, 253, 0.22);
    border-radius: 100px;
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 600;
    color: #487BFD;
    letter-spacing: 0.04em;
    width: fit-content;
`;

const HeroTitle = styled.h1`
    font-size: clamp(40px, 5vw, 64px);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.03em;
    color: #0a0a0a;
    margin: 0;
`;

const GradientText = styled.span`
    background: linear-gradient(135deg, #487BFD 0%, #7B5BFD 50%, #a78bfa 100%);
    background-size: 200% 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 4s ease infinite;
`;

const HeroDesc = styled.p`
    font-size: 17px;
    font-weight: 400;
    color: #777777;
    line-height: 1.75;
    margin: 0;
`;

const HeroCtas = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

/* shimmer 공용 */
const CtaShimmer = styled.span`
    position: absolute;
    top: 0;
    left: -80%;
    width: 50%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent);
    transform: skewX(-15deg);
    pointer-events: none;
`;

const CtaPrimary = styled.button`
    position: relative;
    overflow: hidden;
    background: #487BFD;
    border: none;
    border-radius: 12px;
    padding: 14px 28px;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.15s, box-shadow 0.15s;

    &:hover {
        opacity: 0.92;
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(72,123,253,0.35);
    }
    &:hover ${CtaShimmer} {
        animation: ${shimmerSlide} 0.55s ease forwards;
    }
    &:active { transform: translateY(0); }
`;

const CtaSecondary = styled.button`
    background: transparent;
    border: none;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    color: #888888;
    cursor: pointer;
    padding: 14px 4px;
    transition: color 0.15s;
    &:hover { color: #0a0a0a; }
`;

const TrustRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
`;

const TrustDot = styled.div`
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #c8d5ff;
    flex-shrink: 0;
`;

const TrustText = styled.span`
    font-size: 13px;
    color: #bbbbbb;
`;

const TrustSep = styled.div`
    width: 1px;
    height: 12px;
    background: #e5e5e5;
`;

/* Stats */
const StatsBar = styled.div`
    border-top: 1px solid #f0f0f0;
    border-bottom: 1px solid #f0f0f0;
    background: #fafafa;
`;

const StatsInner = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 40px;
    height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;

    @media (max-width: 576px) {
        padding: 0 24px;
        height: auto;
        flex-wrap: wrap;
        padding: 24px;
        gap: 24px;
    }
`;

const StatItem = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
`;

const StatValue = styled.span`
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: #487BFD;
`;

const StatLabel = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: #aaaaaa;
`;

const StatDivider = styled.div`
    width: 1px;
    height: 40px;
    background: #e8e8e8;
    flex-shrink: 0;
`;

/* Sections */
const Section = styled.section`
    padding: 120px 40px;
    max-width: 1200px;
    margin: 0 auto;
    @media (max-width: 576px) { padding: 80px 24px; }
`;

const AltSection = styled.section`
    padding: 120px 40px;
    background: #f7f9ff;
    @media (max-width: 576px) { padding: 80px 24px; }

    > * {
        max-width: 1200px;
        margin-left: auto;
        margin-right: auto;
    }
`;

/* Use Cases */
const UseCaseSection = styled.section`
    padding: 120px 40px;
    max-width: 1200px;
    margin: 0 auto;
    @media (max-width: 576px) { padding: 80px 24px; }
`;

const UseCaseGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const UseCaseCard = styled.div`
    background: #fafafa;
    border: 1px solid #eeeeee;
    border-radius: 20px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 32px rgba(0,0,0,0.06);
    }
`;

const UseCaseHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const UseCaseEmoji = styled.div`
    font-size: 28px;
    line-height: 1;
`;

const UseCaseIndustry = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: #333333;
`;

const UseCasePain = styled.p`
    font-size: 16px;
    font-weight: 500;
    color: #111111;
    line-height: 1.65;
    margin: 0;
    padding: 16px 20px;
    background: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: -8px;
        left: 24px;
        width: 14px;
        height: 14px;
        background: #ffffff;
        border-top: 1px solid #e8e8e8;
        border-left: 1px solid #e8e8e8;
        transform: rotate(45deg);
    }
`;

const UseCaseResult = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 14px;
    font-weight: 500;
    color: #487BFD;
    line-height: 1.6;
`;

const UseCaseArrow = styled.span`
    flex-shrink: 0;
    font-size: 16px;
    margin-top: 1px;
`;

const SectionLabel = styled.p`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 12px;
`;

const SectionTitle = styled.h2`
    font-size: clamp(28px, 3.5vw, 42px);
    font-weight: 800;
    letter-spacing: -0.03em;
    color: #0a0a0a;
    margin: 0 0 56px;
`;

/* Feature Cards */
const FeatureGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    @media (max-width: 768px) { grid-template-columns: 1fr 1fr; }
    @media (max-width: 576px) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-top: 3px solid ${({ $accent }) => $accent ?? '#487BFD'};
    border-radius: 16px;
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(0,0,0,0.07);
    }
`;

const FeatureIconWrap = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${({ $color }) => $color ? `${$color}14` : '#eef2ff'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
`;

const FeatureTitle = styled.h3`
    font-size: 16px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const FeatureDesc = styled.p`
    font-size: 13px;
    color: #888888;
    line-height: 1.65;
    margin: 0;
`;

/* Steps */
const StepList = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0;
    @media (max-width: 768px) { flex-direction: column; gap: 0; }
`;

const StepItem = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
    position: relative;
`;

const StepNum = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #ffffff;
    border: 2px solid #487BFD;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 800;
    color: #487BFD;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
`;

const StepConnector = styled.div`
    display: ${({ $visible }) => $visible ? 'block' : 'none'};
    position: absolute;
    top: 26px;
    left: calc(50% + 26px);
    right: calc(-50% + 26px);
    height: 2px;
    background: repeating-linear-gradient(
        90deg,
        #c8d5ff 0px,
        #c8d5ff 6px,
        transparent 6px,
        transparent 14px
    );

    @media (max-width: 768px) {
        top: calc(100% + 0px);
        left: 25px;
        right: auto;
        width: 2px;
        height: 32px;
        background: repeating-linear-gradient(
            180deg,
            #c8d5ff 0px,
            #c8d5ff 6px,
            transparent 6px,
            transparent 14px
        );
    }
`;

const StepBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 16px;
`;

const StepTitle = styled.h3`
    font-size: 16px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const StepDesc = styled.p`
    font-size: 13px;
    color: #888888;
    line-height: 1.65;
    white-space: pre-line;
    margin: 0;
`;

/* CTA Banner */
const CtaBanner = styled.section`
    background: linear-gradient(135deg, #1a3a8f 0%, #2d5be3 50%, #487BFD 100%);
    padding: 100px 40px;
    @media (max-width: 576px) { padding: 80px 24px; }
`;

const CtaBannerInner = styled.div`
    max-width: 640px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    text-align: center;
`;

const CtaBannerTitle = styled.h2`
    font-size: clamp(28px, 4vw, 48px);
    font-weight: 800;
    letter-spacing: -0.03em;
    color: #ffffff;
    margin: 0;
`;

const CtaBannerDesc = styled.p`
    font-size: 16px;
    color: rgba(255,255,255,0.65);
    margin: 0 0 8px;
`;

const CtaPrimaryLarge = styled.button`
    position: relative;
    overflow: hidden;
    background: #ffffff;
    border: none;
    border-radius: 14px;
    padding: 16px 36px;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    color: #487BFD;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(0,0,0,0.2);
    }
    &:hover ${CtaShimmer} {
        animation: ${shimmerSlide} 0.55s ease forwards;
    }
    &:active { transform: translateY(0); }
`;

/* Footer */
const Footer = styled.footer`
    border-top: 1px solid #eeeeee;
    padding: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    @media (max-width: 576px) {
        flex-direction: column;
        padding: 32px 24px;
        text-align: center;
    }
`;

const FooterLogo = styled.div`
    font-size: 16px;
    font-weight: 800;
    color: #487BFD;
`;

const FooterText = styled.p`
    font-size: 12px;
    color: #bbbbbb;
    margin: 0;
`;


export default LandingPage;
